<template>
<div class="app-layout">
    <nav
        class="navbar app-layout__navbar"
        role="navigation"
        aria-label="main navigation"
    >
        <div class="navbar-brand">
            <a
                class="logo"
                :href="rootAppUrl"
            >
                <img
                    src="/images/studios-logo.svg"
                    alt="Studios Logo"
                    class="svg svg--large"
                >
            </a>
        </div>

        <div
            v-if="isLoggedIn"
            class="navbar-menu"
            :class="{ 'is-active': isMenuOpen === true }"
        >
            <div class="navbar-end">
                <a
                    class="navbar-link is-arrowless is-hidden-touch"
                    :href="campaignsUrl"
                >
                    My Campaigns
                </a>
                <a
                    class="navbar-link is-arrowless is-hidden-touch"
                    :href="opportunitiesUrl"
                >
                    Opportunities
                </a>
                <div class="navbar-item has-dropdown is-hoverable">
                    <a class="navbar-link is-arrowless navbar__settings is-hidden-touch">
                        <gravatar />
                    </a>
                    <div class="navbar-dropdown is-right">
                        <a
                            class="navbar-item"
                            :href="settingsUrl"
                        >
                            <div class="mr-3">
                                <icon-base
                                    icon-name="Profile"
                                    icon-color="fill--white"
                                    :has-circle="false"
                                    view-box="0 0 18 18"
                                >
                                    <icon-profile />
                                </icon-base>
                            </div>
                            My account
                        </a>
                        <div class="navbar-menu-divider" />
                        <a
                            class="navbar-item"
                            :href="logoutUrl"
                        >
                            <div class="mr-3">
                                <icon-base
                                    icon-name="Logout"
                                    icon-color="fill--white"
                                    :has-circle="false"
                                    view-box="0 0 18 18"
                                >
                                    <icon-logout />
                                </icon-base>
                            </div>
                            Log out
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <a
            v-if="!isLoggedIn"
            :href="loginUrl"
            class="welcome-login is-hidden-mobile"
        >Log In</a>
    </nav>

    <div class="app-layout__scrollable">

        <MaintenanceBanner />
        <div class="pb-footer app-layout__scrollable__body">
            <slot />
        </div>

        <div class="footer app-layout__scrollable__footer">
            <div class="columns is-gapless is-align-items-center">
                <div class="column is-flex is-justify-content-flex-start is-mobile-justify-content-center is-align-items-center footer__copyright">
                    <a
                        href="https://traackr.com"
                    >
                        <img
                            src="/images/studios-logo-simple.svg"
                            alt="Studios Logo"
                            class="svg svg--medium"
                        >
                    </a>
                    <span class="p-2">© {{ currentYear }} Traackr</span>
                </div>

                <div class="column">
                    <div class="footer__links">
                        <a
                            :href="supportEmail"
                            target="_blank"
                            class="is-uppercase"
                        >Support</a>
                        <a
                            href="https://help.traackr.com/creators/en/"
                            rel="noopener noreferrer"
                            target="_blank"
                            class="is-uppercase pl-6"
                        >Help</a>
                        <a
                            href="https://www.traackr.com/tos-studios"
                            target="_blank"
                            class="is-uppercase pl-6"
                        >Terms</a>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <action-menu-mobile
        v-if="isLoggedIn"
        :action-left="{ nav: campaignsUrl, text: 'Campaigns' }"
        :action-middle="{ nav: opportunitiesUrl, text: 'Opportunities'}"
        :action-right="{ nav: profileUrl, text: 'My Account' }"
    >
        <template #iconLeft>
            <icon-base
                icon-name="Briefcase"
                icon-color="fill--transparent"
                :has-circle="false"
                height="24"
                width="24"
            >
                <icon-briefcase />
            </icon-base>
        </template>
        <template #iconMiddle>
            <icon-base
                icon-name="Search"
                icon-color="fill--transparent"
                :has-circle="false"
                height="24"
                width="24"
            >
                <icon-search />
            </icon-base>
        </template>
        <template #iconRight>
            <icon-base
                icon-name="Profile"
                icon-color="fill--none"
                :has-circle="false"
                height="24"
                width="24"
            >
                <icon-profile />
            </icon-base>
        </template>
    </action-menu-mobile>

    <portal-target
        name="modal-portal"
        multiple
    />
</div>
</template>

<script>
import Gravatar from '@/components/Gravatar.vue';
import IconBase from '@/components/IconBase.vue';
import IconProfile from '@/components/icons/IconProfile.vue';
import IconLogout from '@/components/icons/IconLogout.vue';
import IconBriefcase from '@/components/icons/IconBriefcase.vue';
import ActionMenuMobile from '@/components/ActionMenuMobile.vue';
import IconSearch from '@/components/icons/IconSearch.vue';
import MaintenanceBanner from '@/components/MaintenanceBanner.vue';

export default {
    props: {
        /**
         * Whether or not the user is logged in.
         * Used to control the visibility of the navbars / login actions.
         */
        isLoggedIn: {
            type: Boolean,
            default: true
        },
        /**
         * The URL for login actions. Should be overridden if the user should
         * be taken to a specific page after login, using the 'destination'
         * query parameter.
         */
        loginUrl: {
            type: String,
            default: '/login'
        }
    },
    data() {
        return {
            // when the menu is open or not for handling mobile menu
            isMenuOpen: false
        };
    },
    components: {
        Gravatar,
        IconBase,
        IconProfile,
        IconLogout,
        IconBriefcase,
        ActionMenuMobile,
        IconSearch,
        MaintenanceBanner
    },
    computed: {
        /**
         * Provides the current year
         * @returns {String}
         */
        currentYear() {
            return new Date().getFullYear();
        },
        /**
         * Provides the user's full name
         * @returns {String}
         */
        fullName() {
            return this.$store.getters['profile/fullName'];
        },
        /**
         * Provides a root URL for the layout
         * @returns {String}
         */
        rootAppUrl() {
            return this.$store.state.homeUrl;
        },
        /**
         * Provides the URL to get to the campaigns list
         * @returns {String}
         */
        campaignsUrl() {
            return `${this.rootAppUrl}/campaigns/all`;
        },
        /**
         * Provide the URL to the opportunities page
         */
        opportunitiesUrl() {
            return `${this.rootAppUrl}/opportunities`;
        },
        /**
         * Provides logout URL
         * @returns {String}
         */
        logoutUrl() {
            return `${this.rootAppUrl}/logout`;
        },
        /**
         * Provides a settings URL
         * @returns {String}
         */
        settingsUrl() {
            return `${this.rootAppUrl}/settings`;
        },
        /**
         * Provides a profile URL
         * @returns {String}
         */
        profileUrl() {
            return `${this.settingsUrl}#/profile`;
        },
        /**
         * Provides the support email
         * @return {String}
         */
        supportEmail() {
            return `mailto:${window.SAILS_LOCALS?.supportEmail}`;
        }
    },
    methods: {
        /**
         * Gets user profile data from locals and puts it into Vuex
         */
        loadProfileFromLocals() {
            const profileData = window.SAILS_LOCALS?.userMeta;
            if (profileData) {
                this.$store.commit('profile/setProfile', profileData);
            }
        },
        /**
         * Toggles the menu on mobile
         */
        toggleMenu() {
            this.isMenuOpen = !this.isMenuOpen;
        }
    },
    created() {
        this.loadProfileFromLocals();
    },
    mounted() {
        // Bulma makes the root <html> element scrollable
        // This causes a double scroll behavior that impacts the UX negatively
        // Specially on mobile devices
        // We remove this when using AppLayout and rely on the scrollable body container
        document.documentElement.style.overflow = 'hidden';
    }
};
</script>

<style lang="scss" scoped>

.app-layout {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    display: flex;
    flex-flow: column nowrap;
    overflow: hidden;

    &__navbar {
        flex: none;
        position: sticky;
        top: 0;
        padding: 1.5rem 3rem;
        background: transparent;
        z-index: 11;

        .navbar-brand {
            align-items: center;
        }
        .navbar-link {
            padding: 0;
            &:hover {
                background: transparent;
            }
        }
        .navbar-end {
            gap: 3rem;
        }
    }

    &__scrollable {
        flex: 1;
        overflow: auto;
        display: flex;
        flex-flow: column nowrap;

        &__body {
            flex: 1;
        }
        &__footer {
            flex: none;
            padding: 1.5rem 3rem;
            background: #F5F3F2;
        }
    }

}

.logo {
    &__text span {
        line-height: 1;
    }
    &__image {
        width: 36px;
    }
}
</style>
